import React, { useEffect, useState } from "react";
import { Box, Button, Chip, CircularProgress, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { ErrorMessage, useFormikContext } from "formik";
import { consultarCuit } from "../../apiRequests/afip";
import {
  ETIQUETA_IVA,
  esPersonaJuridica,
  mapAfipToClient,
  resumirAfip,
} from "../../utils/afipClientMapper";
import { cuitValido, formatCuit, limpiarCuit, tipoPersona } from "../../utils/cuit";
import { colors } from "../../constants/colors";

// Campos que pisa la consulta. El resto (emails, telefonos, saldo) no lo da ARCA.
const CAMPOS_AUTOCOMPLETABLES = [
  "company",
  "name",
  "lastName",
  "address",
  "province",
  "ivaCondition",
];

const ETIQUETAS = {
  company: "Compañía",
  name: "Nombre",
  lastName: "Apellido",
  address: "Dirección",
  province: "Provincia",
  ivaCondition: "Condición IVA",
};

const CuitLookup = ({ errorStyles }) => {
  const { values, setValues, setFieldValue, setFieldTouched } = useFormikContext();

  const [consultando, setConsultando] = useState(false);
  const [error, setError] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [completados, setCompletados] = useState([]);
  const [vaciados, setVaciados] = useState([]);
  const [juridica, setJuridica] = useState(false);

  const cuit = limpiarCuit(values.cuit);
  const habilitado = cuitValido(cuit) && !consultando;

  // Un CUIT nuevo invalida lo que trajo la consulta anterior.
  useEffect(() => {
    setResumen(null);
    setCompletados([]);
    setVaciados([]);
    setError(null);
  }, [cuit]);

  const consultar = async () => {
    setConsultando(true);
    setError(null);

    try {
      const respuesta = await consultarCuit(cuit);
      const datos = mapAfipToClient(respuesta);

      // Se escriben TODOS los campos, incluso los que ARCA no trae: si no,
      // al consultar otro CUIT quedarian los valores del anterior.
      const conDato = CAMPOS_AUTOCOMPLETABLES.filter((campo) => datos[campo] !== "");
      const limpiados = CAMPOS_AUTOCOMPLETABLES.filter(
        (campo) => datos[campo] === "" && values[campo]
      );

      // De una sola vez: un setFieldValue por campo revalida con valores viejos
      // y deja errores fantasma ("El nombre es obligatorio") sobre campos ya llenos.
      setValues(
        (previos) => ({
          ...previos,
          ...Object.fromEntries(CAMPOS_AUTOCOMPLETABLES.map((c) => [c, datos[c]])),
        }),
        true
      );

      setCompletados(conDato);
      setVaciados(limpiados);
      setJuridica(esPersonaJuridica(respuesta));
      setResumen(resumirAfip(respuesta));

      // La constancia puede fallar y aun asi haber respondido el padron local.
      if (!respuesta.arca && respuesta.arca_error) {
        setError(`Constancia de ARCA no disponible (${respuesta.arca_error}). Se usó el padrón descargable.`);
      }
    } catch (e) {
      setResumen(null);
      setCompletados([]);
      setVaciados([]);
      setError(e.message);
    } finally {
      setConsultando(false);
    }
  };

  const manejarEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault(); // no queremos que dispare el submit del formulario
      if (habilitado) consultar();
    }
  };

  const cambiarCuit = (e) => {
    setFieldValue("cuit", limpiarCuit(e.target.value).slice(0, 11));
  };

  return (
    <Box className="cuit_lookup">
      <p className="label">Cuit</p>

      <Box className="cuit_lookup_row">
        <input
          className="input cuit_lookup_input"
          name="cuit"
          value={values.cuit || ""}
          onChange={cambiarCuit}
          onBlur={() => setFieldTouched("cuit", true)}
          onKeyDown={manejarEnter}
          inputMode="numeric"
          maxLength={11}
          placeholder="30500010912"
          autoFocus
        />

        <Tooltip title="Trae razón social, domicilio y condición de IVA desde ARCA">
          <span>
            <Button
              variant="contained"
              onClick={consultar}
              disabled={!habilitado}
              startIcon={consultando ? null : <SearchIcon />}
              sx={{
                backgroundColor: colors.red,
                "&:hover": { backgroundColor: colors.red },
                textTransform: "none",
                borderRadius: "30px",
                minWidth: "150px",
                height: "40px",
              }}
            >
              {consultando ? (
                <CircularProgress size={18} sx={{ color: colors.white }} />
              ) : (
                "Obtener datos"
              )}
            </Button>
          </span>
        </Tooltip>
      </Box>

      <ErrorMessage name="cuit" component="p" style={errorStyles} />

      {cuit.length === 11 && cuitValido(cuit) && (
        <p className="cuit_lookup_hint">
          {formatCuit(cuit)} — {tipoPersona(cuit)}
        </p>
      )}

      {error && <p className="cuit_lookup_error">{error}</p>}

      {resumen && (
        <Box className="cuit_lookup_result">
          <Box className="cuit_lookup_result_head">
            <strong>{resumen.razonSocial}</strong>
            <span className="cuit_lookup_source">{resumen.fuente}</span>
          </Box>

          <Box className="cuit_lookup_tags">
            {resumen.condicionIva && (
              <Chip
                size="small"
                label={ETIQUETA_IVA[resumen.condicionIva] || resumen.condicionIva}
              />
            )}
            {resumen.categoriaMonotributo && (
              <Chip size="small" label={resumen.categoriaMonotributo} />
            )}
            {resumen.estadoClave && <Chip size="small" label={`Clave: ${resumen.estadoClave}`} />}
          </Box>

          {resumen.domicilio && (
            <p className="cuit_lookup_line">
              <span>Domicilio fiscal:</span> {resumen.domicilio}
            </p>
          )}
          {resumen.actividad && (
            <p className="cuit_lookup_line">
              <span>Actividad principal:</span> {resumen.actividad}
            </p>
          )}

          {completados.length > 0 && (
            <p className="cuit_lookup_filled">
              Se completaron: {completados.map((c) => ETIQUETAS[c]).join(", ")}. Revisá
              los datos y completá emails y teléfonos.
            </p>
          )}

          {vaciados.length > 0 && (
            <p className="cuit_lookup_cleared">
              Se limpiaron ({vaciados.map((c) => ETIQUETAS[c]).join(", ")}): ARCA no
              los devuelve para este CUIT y eran del CUIT anterior.
            </p>
          )}

          {juridica && (
            <p className="cuit_lookup_hint_juridica">
              Es persona jurídica: ARCA no devuelve una persona, así que se copió la
              razón social en <strong>Nombre</strong> y <strong>Apellido</strong>.
              Cambialos por la persona de contacto si la conocés — son los que
              figuran como “Firmado por” en la orden de servicio.
            </p>
          )}
        </Box>
      )}
    </Box>
  );
};

export default CuitLookup;
